import { Checkbox, Icon, IconNames, Skeleton, Spinner, Text, Tooltip } from '@sendible/design-system';
import { generateId, getAvatarInitials, getColorTone } from '@sendible/design-system/src/components/utils/helpers';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useInView } from 'react-intersection-observer';
import { MediaIdType, useBridgeUser } from '@sendible/shared-state-bridge';
import { MediaPreview } from '@sendible/frontendv2/src/components';
import { PdfThumbnail } from '@sendible/frontendv2/src/components/MediaSystem/components/MediaBelt/PdfThumbnail';
import {
  Container,
  ContentArea,
  InfoArea,
  Title,
  ThumbArea,
  SocialArea,
  ThumbHighlight,
  ProfileList,
  FooterArea,
  Avatar,
  ClampTooltip,
} from './index.styles.ts';
import {
  formatTimeLabelNoTransform,
  getChannelIcon,
  getDeliveryStatusIcon,
  getDeliveryStatusTooltip,
  getCalendarThumbnailUrl,
  getIsPartialModeSuccess,
  isPdfMedia,
  processTags,
} from '../../utils.ts';
import { useCalendarMediaList } from '../../hooks.ts';
import { canDragMessage } from '../../utils.ts';
import { ProgressIndicator } from '../ProgressIndicator/index.tsx';
import { useCalendarContext } from '../../context.tsx';
import { useSelectionContext } from '../../selectionContext.tsx';

export interface Props {
  id: CalendarMessage['id'];
  isDragging?: boolean;
  deliveryStatus: CalendarMessage['deliveryStatus'];
  destinations: CalendarDestination[];
  medias?: CalendarMedia[];
  onClick: () => void;
  postAt: string;
  recurrence?: CalendarRecurrence;
  status: CalendarStatuses;
  title: string;
  type: 'monthly' | 'weekly' | 'daily' | 'preview' | 'compact';
  workspace: CalendarWorkspace;
}

const CalendarCardInner = ({
  deliveryStatus,
  destinations,
  id,
  isDragging = false,
  medias,
  onClick,
  postAt,
  recurrence,
  status,
  title,
  type,
  workspace,
}: Props) => {
  const user = useBridgeUser();
  const { filterParams, isExporting } = useCalendarContext();
  const { isSelected, selectedMessageIds, toggleMessage } = useSelectionContext();
  const isMessageSelected = isSelected(id);
  const showCheckboxAlways = selectedMessageIds.length > 0;
  const primaryMedia = medias?.[0];
  const showThumb = Boolean(primaryMedia?.url) && type !== 'compact';
  const showPdfThumb = showThumb && !!primaryMedia && isPdfMedia(primaryMedia);
  const [isLoadingImage, setIsLoadingImage] = useState(Boolean(showThumb && !showPdfThumb));
  const [showProfiles, setShowProfiles] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { t, i18n } = useTranslation(['calendar', 'common_terms']);
  const profilesElRef = useRef<HTMLDivElement | null>(null);
  const socialAreaElRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const cardIdRef = useRef<string | null>(null);

  if (cardIdRef.current == null) cardIdRef.current = generateId();

  const { isPaused } = user;
  const isDraggable = type !== 'daily' && canDragMessage({ status, recurrence });
  const isDisabled = status === 'Processing';

  const uniqueChannelIcons = destinations.reduce<IconNames[]>((acc, destination) => {
    const icon = destination?.channelType ? getChannelIcon(destination.channelType) : undefined;

    if (!icon || acc.includes(icon)) return acc;

    return [...acc, icon];
  }, []);
  const visibleChannelIcons = uniqueChannelIcons.length > 3 ? uniqueChannelIcons.slice(0, 2) : uniqueChannelIcons;
  const hiddenChannelIconsCount = uniqueChannelIcons.length > 3 ? uniqueChannelIcons.length - visibleChannelIcons.length : 0;
  const destinationRecipientsCount = destinations.reduce((total, destination) => total + (destination.channelSize ?? 1), 0);
  const { color, name } = workspace;
  const colorTone = getColorTone(color);

  const hasMultipleMedia = Boolean(medias && medias.length > 1);
  const clockFormat = user?.clockFormat ?? 12;

  const TOOLTIP_DELAY = 250;

  const {
    x,
    y,
    strategy,
    refs: { setReference, setFloating },
  } = useFloating({
    open: showProfiles,
    onOpenChange: setShowProfiles,
    placement: 'bottom-end',
    middleware: [offset(0), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const handleInViewChange = useCallback(
    (isInView: boolean) => {
      if (showProfiles && !isInView) {
        setShowProfiles(false);
      }
    },
    [showProfiles]
  );

  const { ref: inViewRef } = useInView({
    threshold: 1,
    rootMargin: '8px',
    onChange: handleInViewChange,
  });

  /** There are some UX complexities that requires the consts and logic related to them below:
   * Usually the status icon and tooltip is based on the status, but there are edge case:
   * If the message has bounced messages, we show a percentage indicator around the icon showing the percentage of messages
   * that bounced (and tooltip).
   * If the "Sent" filter is selected but not the "Bounced" filter, we consider bounced messages as "Sent" for the purpose
   * of the icon and tooltip, showing a success icon and showing in the tooltip how many messages bounced vs how many were sent.
   */

  const isPartialModeSuccess = getIsPartialModeSuccess(filterParams);
  const shouldShowPausedLabel = isPaused && status !== 'Sent' && status !== 'Failed';
  const deliveryStatusIcon = getDeliveryStatusIcon(status, !!recurrence, deliveryStatus, isPartialModeSuccess);

  const getStatusTooltip = () =>
    getDeliveryStatusTooltip({
      recurrence,
      deliveryStatus,
      status,
      isPartialModeSuccess,
      isPaused,
      t,
      language: i18n.language,
    });

  const handleCardClick = useCallback(() => {
    if (!isDisabled) onClick();
  }, [isDisabled, onClick]);

  const previewableMedias = medias || [];
  const mediaList = useCalendarMediaList(previewableMedias, showPreview);

  const handleThumbClick = () => {
    if (!medias?.length) return;

    setShowPreview(true);
  };

  const handleDownload = async (action: string, mediaId: MediaIdType) => {
    if (action !== 'download') return;

    const media = mediaList.find((m) => m.id === mediaId);

    if (!media) return;

    const response = await fetch(media.downloadUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const tempLink = document.createElement('a');

    tempLink.href = blobUrl;
    tempLink.setAttribute('download', media.name);
    tempLink.click();
    tempLink.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const infoArea = (
    <Tooltip
      noDecoration
      content={getStatusTooltip()}
      maxWidth
      delay={TOOLTIP_DELAY}
    >
      <InfoArea
        data-testid="calendar-info-area"
        $type={type}
      >
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- boundary only stops the click from reaching the card; the checkbox inside is the actual interactive control */}
        <span
          className="info-area-icon-slot"
          onClick={(e) => e.stopPropagation()}
        >
          {/* data-export-hide keeps the selection checkbox out of the exported/printed HTML snapshot,
              same convention as the other UI-only controls excluded by exportCalendarSectionHtml. */}
          <span
            data-export-hide
            style={{ display: 'contents' }}
          >
            <Checkbox
              ariaLabel={t('select_post') as string}
              check={() => toggleMessage(id)}
              initialState={isMessageSelected ? 'checked' : 'unchecked'}
              isDisabled={isDisabled}
              size="sm"
              testId="calendar-card-checkbox"
            />
          </span>
          {/* Its own positioning context so the progress ring (absolutely centered on it) tracks
              the icon as the checkbox slides in and pushes it, instead of staying pinned to
              where the icon used to sit. */}
          <span className="info-area-status-icon">
            {status === 'Processing' ? (
              <Spinner size="xs" />
            ) : (
              deliveryStatusIcon && (
                <Icon
                  name={deliveryStatusIcon.icon}
                  color={deliveryStatusIcon.color}
                  strokeWidth={1.8}
                />
              )
            )}
            {deliveryStatusIcon?.progress && (
              <ProgressIndicator
                percent={deliveryStatusIcon.progress.percent}
                type={deliveryStatusIcon.progress.type}
              />
            )}
          </span>
        </span>
        <p>{formatTimeLabelNoTransform(postAt, clockFormat)}</p>
      </InfoArea>
    </Tooltip>
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { detail } = e as CustomEvent<{ sourceId: string }>;

      if (!detail) return;
      if (detail.sourceId !== cardIdRef.current) {
        setShowProfiles(false);
      }
    };

    window.addEventListener('calendar:close-profiles', handler as EventListener);

    return () => window.removeEventListener('calendar:close-profiles', handler as EventListener);
  }, []);

  useEffect(() => {
    if (!showProfiles) return;

    const handleClickOutside = (event: MouseEvent) => {
      const floatingEl = profilesElRef.current;
      const referenceEl = socialAreaElRef.current;
      const target = event.target as Node;
      const path = (event.composedPath && event.composedPath()) || [];

      const isInside = (el: HTMLElement | null) => !!el && (el.contains(target) || path.includes(el));

      if (!isInside(floatingEl) && !isInside(referenceEl)) {
        setShowProfiles(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showProfiles]);

  useEffect(() => {
    if (!showPdfThumb && primaryMedia?.url && imgRef.current?.complete) setIsLoadingImage(false);
  }, [primaryMedia?.url, showPdfThumb]);

  return (
    <>
      <Container
        data-testid="calendar-card"
        className={`calendar-card publish_calendar_post_${status.toLowerCase()}`}
        id={`calendar-card-message-${id}`}
        $isDragging={isDragging}
        $isDraggable={isDraggable}
        $isDisabled={isDisabled}
        $isPaused={shouldShowPausedLabel}
        $type={type}
        $showCheckboxAlways={showCheckboxAlways}
        onClick={handleCardClick}
      >
        {type === 'daily' && infoArea}
        {showThumb && (
          <ThumbArea
            data-testid="calendar-card-thumbnail-area"
            className="calendar-card-thumb-area"
            data-export-medias={
              isExporting
                ? JSON.stringify(
                    (medias ?? []).map((md) => ({ url: md.url, thumbnailUrl: md.thumbnailUrl, type: md.type, isLinkPreview: md.isLinkPreview }))
                  )
                : ''
            }
            onClick={(e) => {
              e.stopPropagation();
              handleThumbClick();
            }}
            $isLoading={isLoadingImage}
            $type={type}
          >
            {isLoadingImage && (
              <Skeleton
                height="100%"
                width="100%"
              />
            )}
            {(hasMultipleMedia ||
              primaryMedia?.isLinkPreview ||
              primaryMedia?.type === 'Video' ||
              primaryMedia?.type === 'Document' ||
              primaryMedia?.type === 'Gif') && (
              <ThumbHighlight>
                {hasMultipleMedia ? (
                  <Icon
                    name="medias"
                    color="brandLight"
                    strokeWidth={1.8}
                  />
                ) : primaryMedia?.isLinkPreview ? (
                  <Icon
                    name="link"
                    color="brandLight"
                    strokeWidth={1.8}
                  />
                ) : primaryMedia?.type === 'Gif' ? (
                  <span>GIF</span>
                ) : (
                  <Icon
                    name={primaryMedia?.type === 'Video' ? 'play' : 'file'}
                    color="brandLight"
                    strokeWidth={1.8}
                  />
                )}
              </ThumbHighlight>
            )}
            {showPdfThumb ? (
              <PdfThumbnail
                url={primaryMedia!.url}
                altText=""
              />
            ) : (
              <img
                alt=""
                onLoad={() => setIsLoadingImage(false)}
                onError={() => setIsLoadingImage(false)}
                ref={imgRef}
                loading="lazy"
                decoding="async"
                src={
                  primaryMedia?.type === 'Video' && primaryMedia?.thumbnailUrl
                    ? primaryMedia.thumbnailUrl
                    : getCalendarThumbnailUrl(primaryMedia!, user)
                }
              />
            )}
          </ThumbArea>
        )}
        <ContentArea
          data-testid="calendar-card-content-area"
          className="calendar-card-content-area"
          $type={type}
        >
          {type !== 'daily' && infoArea}
          {type !== 'compact' && (
            <Tooltip
              noDecoration
              content={<ClampTooltip>{processTags(title)}</ClampTooltip>}
              maxWidth
              delay={TOOLTIP_DELAY * 2}
            >
              <Title $type={type}>{processTags(title)}</Title>
            </Tooltip>
          )}
          <FooterArea $type={type}>
            {type !== 'compact' && (
              <Tooltip
                noDecoration
                content={name}
                maxWidth
                delay={TOOLTIP_DELAY}
              >
                <Avatar
                  $color={color}
                  $isDark={colorTone === 'dark'}
                  data-export-workspace={name}
                >
                  <span>{getAvatarInitials(name)}</span>
                </Avatar>
              </Tooltip>
            )}
            <Tooltip
              noDecoration
              content={
                destinationRecipientsCount > 1
                  ? t('see_profiles', { ns: 'calendar', count: destinationRecipientsCount })
                  : destinations[0]?.channelName
              }
              maxWidth
              delay={TOOLTIP_DELAY}
            >
              <SocialArea
                data-export-profiles={JSON.stringify(destinations.map((d) => ({ name: d?.channelName ?? '', channelType: d?.channelType ?? '' })))}
                onClick={(e) => {
                  e.stopPropagation();
                  window.dispatchEvent(new CustomEvent('calendar:close-profiles', { detail: { sourceId: cardIdRef.current } }));
                  setShowProfiles((prev) => !prev);
                }}
                ref={(node: HTMLDivElement | null) => {
                  setReference(node);
                  socialAreaElRef.current = node;
                }}
                $type={type}
              >
                <div>
                  {visibleChannelIcons.map((icon, idx) => (
                    <Icon
                      key={`${icon}-${idx}`}
                      name={icon}
                    />
                  ))}
                  {hiddenChannelIconsCount > 0 && <span>+{hiddenChannelIconsCount}</span>}
                </div>
              </SocialArea>
            </Tooltip>
          </FooterArea>
        </ContentArea>
      </Container>

      {showProfiles &&
        createPortal(
          <ProfileList
            ref={(node) => {
              setFloating(node);
              inViewRef(node);
              profilesElRef.current = node;
            }}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            <ul>
              {destinations.map((d) => {
                const icon = d?.channelType ? getChannelIcon(d.channelType) : undefined;

                return (
                  <Tooltip
                    key={`${d?.channelType}-${d?.channelId ?? d?.channelName}`}
                    noDecoration
                    content={d?.channelName || ''}
                    maxWidth
                    delay={TOOLTIP_DELAY}
                  >
                    <li>
                      {icon && (
                        <Icon
                          name={icon}
                          strokeWidth={1.6}
                        />
                      )}
                      <Text
                        variation="body_12"
                        color="brandDarkTint30"
                        maxLines={1}
                      >
                        {d?.channelName}
                      </Text>
                    </li>
                  </Tooltip>
                );
              })}
            </ul>
          </ProfileList>,
          document.getElementById('dropdown-root') as HTMLElement
        )}
      {showPreview && mediaList.length > 0 && (
        <MediaPreview
          initialMediaIndex={0}
          mediaList={mediaList}
          supportedActions={['download']}
          onActionClick={handleDownload}
          onClose={() => setShowPreview(false)}
          shouldShowActions
        />
      )}
    </>
  );
};

export const CalendarCard = memo(CalendarCardInner);
